/**
 * @module useHotkey
 *
 * @see https://0.vuetifyjs.com/composables/system/use-hotkey
 *
 * @remarks
 * Hotkey listener composable with key combination and sequence support.
 *
 * Key features:
 * - Key combination support (`ctrl+k`, `shift+enter`)
 * - Key sequence support (`g-h` for GitHub-style shortcuts)
 * - Platform-aware modifiers (Mac: cmd→meta, others: cmd→ctrl)
 * - Input focus detection (skip when typing in inputs)
 * - Sequence timeout with automatic reset
 * - Key and symbol word alias normalization (`plus` → `+`, `esc` → `escape`, etc.)
 * - Pause/resume/stop functionality
 * - Automatic cleanup on scope disposal
 * - SSR-safe (no-op when not in browser)
 *
 * Builds on useEventListener for lifecycle-managed keyboard events.
 *
 * @example
 * ```ts
 * import { useHotkey } from '@vuetify/v0'
 *
 * useHotkey('ctrl+k', () => console.log('opened palette'))
 * useHotkey('g-h', () => console.log('go home'))
 * ```
 */

// Composables
import { useWindowEventListener } from '#v0/composables/useEventListener'

// Globals
import { IN_BROWSER } from '#v0/constants/globals'

import { splitKeyCombination, splitKeySequence, MODIFIERS } from './parsing'

// Utilities
import { getActiveElement, isNull, isUndefined } from '#v0/utilities'
import { onScopeDispose, shallowReadonly, shallowRef, toRef, toValue, watch } from 'vue'

// Types
import type { Modifier } from './parsing'
import type { MaybeRefOrGetter, Ref } from 'vue'

export interface UseHotkeyOptions {
  /**
   * The keyboard event type to listen for.
   * @default 'keydown'
   */
  event?: MaybeRefOrGetter<'keydown' | 'keyup'>
  /**
   * Whether to trigger the callback when an input element is focused.
   * @default false
   */
  inputs?: MaybeRefOrGetter<boolean>
  /**
   * Whether to prevent the default browser action.
   * @default true
   */
  preventDefault?: MaybeRefOrGetter<boolean>
  /**
   * Whether to stop event propagation.
   * @default false
   */
  stopPropagation?: MaybeRefOrGetter<boolean>
  /**
   * Timeout in ms before a key sequence resets.
   * @default 1000
   */
  sequenceTimeout?: MaybeRefOrGetter<number>
}

export interface UseHotkeyReturn {
  /**
   * Whether the hotkey listener is currently active (listening for keys).
   * False when paused, when keys is undefined, or in SSR.
   */
  readonly isActive: Readonly<Ref<boolean>>
  /**
   * Whether the hotkey listener is currently paused.
   */
  readonly isPaused: Readonly<Ref<boolean>>
  /**
   * Pause listening (removes listener but keeps configuration).
   */
  pause: () => void
  /**
   * Resume listening after pause.
   */
  resume: () => void
  /**
   * Stop listening and clean up (removes listener).
   */
  stop: () => void
}

/**
 * Platform context for testing platform-specific behavior.
 * @internal
 */
export interface PlatformContext {
  isMac: boolean
}

// Cache platform detection at module level (includes iOS/iPadOS for Apple keyboard support)
const isMac = IN_BROWSER && /mac|iphone|ipad|ipod/i.test(navigator?.userAgent ?? '')

/**
 * A composable that listens for hotkey combinations and sequences.
 *
 * @param keys - The hotkey string (e.g., 'ctrl+k', 'g-h')
 * @param callback - The function to call when the hotkey is triggered
 * @param options - Configuration options
 * @returns An object with state refs and control methods
 *
 * @see https://0.vuetifyjs.com/composables/system/use-hotkey
 *
 * @example
 * ```ts
 * import { useHotkey } from '@vuetify/v0'
 *
 * // Simple combination
 * const { isActive, pause, resume } = useHotkey('ctrl+k', () => {
 *   console.log('Command palette opened')
 * })
 *
 * // Key sequence (GitHub-style)
 * useHotkey('g-h', () => console.log('Go home'))
 *
 * // With options
 * useHotkey('escape', closeModal, { inputs: true })
 *
 * // Pause/resume control
 * pause()  // Temporarily disable
 * resume() // Re-enable
 * ```
 */
export function useHotkey (
  keys: MaybeRefOrGetter<string | undefined>,
  callback: (e: KeyboardEvent) => void,
  options: UseHotkeyOptions = {},
  _platform?: PlatformContext,
): UseHotkeyReturn {
  const platformIsMac = _platform?.isMac ?? isMac
  const {
    event = 'keydown',
    inputs = false,
    preventDefault = true,
    stopPropagation = false,
    sequenceTimeout = 1000,
  } = options

  const isPaused = shallowRef(false)
  const cleanup = shallowRef<(() => void) | null>(null)
  const isActive = toRef(() => !isNull(cleanup.value))

  let sequenceTimer: ReturnType<typeof setTimeout> | undefined
  let keyGroups: string[] = []
  let isSequence = false
  let groupIndex = 0

  function isInputFocused (): boolean {
    /* v8 ignore next -- IN_BROWSER always true in happy-dom test env */
    if (!IN_BROWSER) return false
    if (toValue(inputs)) return false

    const activeElement = getActiveElement() as HTMLElement | null
    /* v8 ignore next -- defensive: document.activeElement is always set in happy-dom */
    if (!activeElement) return false

    // Check ARIA roles for custom text inputs
    const role = activeElement.getAttribute('role')
    if (role === 'textbox' || role === 'searchbox' || role === 'combobox' || role === 'spinbutton') {
      return true
    }

    // Check native text inputs (not checkbox, radio, submit, etc.)
    if (activeElement.tagName === 'INPUT') {
      const type = (activeElement as HTMLInputElement).type?.toLowerCase()
      const textTypes = ['text', 'email', 'password', 'search', 'tel', 'url', 'number', 'date', 'datetime-local', 'time', 'month', 'week']
      return textTypes.includes(type)
    }

    return (
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable
    )
  }

  function resetSequence () {
    groupIndex = 0
    clearTimeout(sequenceTimer)
  }

  function handler (e: KeyboardEvent) {
    const group = keyGroups[groupIndex]

    if (!group || isInputFocused()) return

    if (!matchesKeyGroup(e, group, platformIsMac)) {
      if (isSequence) resetSequence()
      return
    }

    if (toValue(stopPropagation)) e.stopPropagation()
    if (toValue(preventDefault)) e.preventDefault()

    if (!isSequence) {
      callback(e)
      return
    }

    clearTimeout(sequenceTimer)
    groupIndex++

    if (groupIndex === keyGroups.length) {
      callback(e)
      resetSequence()
      return
    }

    sequenceTimer = setTimeout(resetSequence, toValue(sequenceTimeout))
  }

  function setup () {
    if (!IN_BROWSER || isPaused.value) return

    const currentKeys = toValue(keys)
    if (!currentKeys) return

    const groups = splitKeySequence(currentKeys.toLowerCase())
    /* v8 ignore next -- defensive: splitKeySequence rejects empty strings earlier */
    if (groups.length === 0) return

    isSequence = groups.length > 1
    keyGroups = groups
    resetSequence()

    cleanup.value = useWindowEventListener(toValue(event), handler)
  }

  function dispose () {
    if (cleanup.value) {
      cleanup.value()
      cleanup.value = null
    }
    clearTimeout(sequenceTimer)
    keyGroups = []
    isSequence = false
    groupIndex = 0
  }

  function pause () {
    isPaused.value = true
    dispose()
  }

  function resume () {
    isPaused.value = false
    setup()
  }

  function stop () {
    dispose()
  }

  watch(() => toValue(keys), () => {
    if (isPaused.value) return
    dispose()
    setup()
  }, { immediate: true })

  watch(() => toValue(event), () => {
    if (isPaused.value || !toValue(keys)) return
    dispose()
    setup()
  })

  onScopeDispose(stop, true)

  return {
    isActive: shallowReadonly(isActive),
    isPaused: shallowReadonly(isPaused),
    pause,
    resume,
    stop,
  }
}

interface ParsedKeyGroup {
  modifiers: Record<Modifier, boolean>
  actualKey: string | undefined
}

function isModifier (key: string): key is Modifier {
  return (MODIFIERS as readonly string[]).includes(key)
}

function parseKeyGroup (group: string): ParsedKeyGroup {
  const { keys: parts } = splitKeyCombination(group.toLowerCase())

  const modifiers = Object.fromEntries(
    MODIFIERS.map(m => [m, false]),
  ) as Record<Modifier, boolean>

  /* v8 ignore next 3 -- defensive: parseKeyGroup is not invoked with empty parts */
  if (parts.length === 0) {
    return { modifiers, actualKey: undefined }
  }

  let actualKey: string | undefined

  for (const part of parts) {
    if (isModifier(part)) {
      modifiers[part] = true
    } else {
      actualKey = part
    }
  }

  return { modifiers, actualKey }
}

function matchesKeyGroup (e: KeyboardEvent, group: string, platformIsMac: boolean): boolean {
  const { modifiers, actualKey } = parseKeyGroup(group)

  const expectCtrl = modifiers.ctrl || (!platformIsMac && (modifiers.cmd || modifiers.meta))
  const expectMeta = platformIsMac && (modifiers.cmd || modifiers.meta)

  const modifiersMatch = (
    e.ctrlKey === expectCtrl &&
    e.metaKey === expectMeta &&
    e.shiftKey === modifiers.shift &&
    e.altKey === modifiers.alt
  )

  // If no actual key specified, only match modifiers
  if (isUndefined(actualKey)) return modifiersMatch

  return modifiersMatch && e.key.toLowerCase() === actualKey.toLowerCase()
}
